import React from 'react';
import { Typography, Badge, Tag } from 'antd';
import { useTranslation } from '../../hooks/useTranslation';

const { Title, Text } = Typography;

interface CallHeaderProps {
  appointmentId?: string;
  isConnecting: boolean;
  isInCall: boolean;
  callDuration: number;
  networkQuality: 'excellent' | 'good' | 'poor' | 'bad' | 'very bad' | 'down';
  remoteUsersCount: number;
  audioDeviceError: string;
  localTracks: {
    audioTrack?: any;
    videoTrack?: any;
  };
  isAudioMuted: boolean;
  formatCallDuration: (seconds: number) => string;
}

const CallHeader: React.FC<CallHeaderProps> = ({
  appointmentId,
  isConnecting,
  isInCall,
  callDuration,
  networkQuality,
  remoteUsersCount,
  audioDeviceError,
  localTracks,
  isAudioMuted,
  formatCallDuration,
}) => {
  const { t } = useTranslation();

  const getNetworkQualityText = (quality: string) => {
    switch (quality) {
      case 'excellent': return 'Excellent';
      case 'good': return 'Good';
      case 'poor': return 'Poor';
      case 'bad': return 'Bad';
      case 'very bad': return 'Very Bad';
      case 'down': return 'Down';
      default: return 'Unknown';
    }
  };

  const getNetworkQualityColor = (quality: string) => {
    if (quality === 'excellent' || quality === 'good') {
      return {
        backgroundColor: 'rgba(82, 196, 26, 0.2)',
        borderColor: '#52c41a',
        dotColor: '#52c41a'
      };
    } else if (quality === 'poor') {
      return {
        backgroundColor: 'rgba(250, 173, 20, 0.2)',
        borderColor: '#faad14',
        dotColor: '#faad14'
      };
    } else {
      return {
        backgroundColor: 'rgba(255, 77, 79, 0.2)',
        borderColor: '#ff4d4f',
        dotColor: '#ff4d4f'
      };
    }
  };

  const getAudioStatusText = () => {
    if (audioDeviceError) return 'Audio Error';
    if (localTracks.audioTrack) {
      return isAudioMuted ? 'Muted' : 'Audio On';
    }
    return 'No Audio';
  };

  const getAudioStatusColor = () => {
    if (audioDeviceError) return '#ff4d4f';
    if (localTracks.audioTrack) {
      return isAudioMuted ? '#faad14' : '#52c41a';
    }
    return '#d9d9d9';
  };

  const networkColors = getNetworkQualityColor(networkQuality);

  return (
    <div style={{
      padding: '16px 24px',
      backgroundColor: 'rgba(0,0,0,0.8)',
      color: 'white',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    }}>
      <div>
        <Title level={4} style={{ color: 'white', margin: 0 }}>
          {t('agora.videoConsultation')}
        </Title>
        <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
          {appointmentId?.slice(-6)}
        </Text>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Badge
          status={isConnecting ? "processing" : isInCall ? "success" : "default"}
          text={isConnecting ? t('agora.connecting') : isInCall ? t('agora.connected') : t('agora.disconnected')}
        />
        {isInCall && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text style={{ color: 'white' }}>
              {formatCallDuration(callDuration)}
            </Text>
            {/* Network Quality Indicator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 6px',
              borderRadius: 4,
              backgroundColor: networkColors.backgroundColor,
              border: `1px solid ${networkColors.borderColor}`
            }}>
              <div style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: networkColors.dotColor
              }} />
              <Text style={{
                color: 'white',
                fontSize: 10,
                fontWeight: 500
              }}>
                {getNetworkQualityText(networkQuality)}
              </Text>
            </div>
          </div>
        )}
        {remoteUsersCount > 0 && (
          <Tag color="green">{remoteUsersCount} {t('agora.participants')}</Tag>
        )}
        {/* Audio Status Indicator */}
        {isInCall && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: getAudioStatusColor()
            }} />
            <Text style={{ color: 'white', fontSize: 12 }}>
              {getAudioStatusText()}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallHeader;
